 

import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Mail, Calendar, CheckCircle2, PlayCircle, Activity, Info } from "lucide-react";
import {Link} from 'react-router-dom';


const GastricBalloon = () => {
  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20">
      
      {/* Hero Section */}
      <section className="relative bg-[#800000] text-white py-20 px-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-white/10 skew-x-12 transform translate-x-20" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-1.5 rounded-full mb-6 border border-white/30">
            <Activity className="w-4 h-4 text-white" />
             <span className="text-sm font-medium text-white">
              Big Bariatrics
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            Gastric Balloon
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 -mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Sidebar */}
        <aside className="lg:col-span-3 space-y-6">
          <Card className="sticky top-6 shadow-xl border-none bg-white/90 backdrop-blur-md">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Info className="w-5 h-5 text-[#800000]" /> Patient Portal
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              <div className="mt-4 p-4 bg-[#800000] rounded-2xl text-white shadow-lg">
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Book Appointment
                </h4>
                <p className="text-xs text-white/80 mb-4">
                  Schedule a consultation to see if Gastric Balloon is right for you.
                </p>
               <Link to="/contact">
                <Button className="w-full bg-white text-[#800000] hover:bg-gray-100 font-bold">
                  Reserve Now
                </Button>
                </Link>
                <div className="mt-4 pt-4 border-t border-white/20 space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Phone className="w-3 h-3" /> +91-755-4055123
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-3 h-3" /> info@bigbariatrics.com
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* Main Content */}
        <main className="lg:col-span-9 space-y-12">

          {/* Procedure Section */}
          <section className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1 space-y-6 text-gray-700">
                <p className="text-xl leading-relaxed">
                  Gastric Balloon is a minimally invasive, non-surgical weight loss procedure. A soft, inflatable balloon is placed in your stomach to reduce the amount of food you can eat at one time, helping you feel full faster.
                </p>
                <p className="text-lg leading-relaxed">
                  This procedure is temporary, usually for 6 months, and is ideal for individuals who want to jumpstart weight loss before considering surgical options.
                </p>

                <div className="w-full bg-gray-100 rounded-2xl overflow-hidden shadow-inner  flex items-center justify-center p-1">
                  <img 
                    src="/gastric-ballon.png" 
                    alt="Gastric Balloon Procedure"
                    className="w-full h-full object-fit max-h-96  max-w-96"
                  />
                </div>

                <p className="bg-[#fff0f0] border-l-4 border-[#800000] p-4 italic rounded-r-lg">
                  The Gastric Balloon helps reduce food intake and encourages behavioral changes in eating habits, serving as an effective bridge to long-term lifestyle modifications.
                </p>

                {/* Benefits */}
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    Key Benefits
                  </h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li>Non-surgical and minimally invasive</li>
                    <li>Temporary solution with effective weight loss</li>
                    <li>Supports lifestyle and dietary changes</li>
                    <li>Short procedure with quick recovery</li>
                  </ul>
                </div>

                {/* Ideal Candidates */}
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    Ideal Candidates
                  </h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li>Individuals with moderate obesity (BMI 27-35)</li>
                    <li>Those looking for non-surgical weight loss options</li>
                    <li>Patients motivated to adopt lifestyle changes</li>
                  </ul>
                </div>

              </div>
            </div>
          </section>

          {/* Recovery Section */}
          <section>
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Recovery & Follow-Up
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 rounded-3xl overflow-hidden shadow-lg border border-gray-100">
              <div className="bg-white p-8 text-center">
                <div className="w-12 h-12 bg-[#800000]/10 text-[#800000] rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  01
                </div>
                <h4 className="font-bold mb-2">Week 1</h4>
                <p className="text-sm text-gray-600">Liquid diet & adjustment</p>
              </div>
              <div className="bg-[#800000]/5 p-8 text-center">
                <div className="w-12 h-12 bg-[#800000] text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  02
                </div>
                <h4 className="font-bold mb-2">Weeks 2-4</h4>
                <p className="text-sm text-gray-600">Soft foods and adaptation</p>
              </div>
              <div className="bg-white p-8 text-center">
                <div className="w-12 h-12 bg-[#800000]/10 text-[#800000] rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  03
                </div>
                <h4 className="font-bold mb-2">Month 2+</h4>
                <p className="text-sm text-gray-600">Healthy eating & lifestyle changes</p>
              </div>
            </div>
          </section>

          {/* Video Section */}
          {/* <section className="bg-[#800000] rounded-3xl p-10 text-white">
            <h3 className="text-2xl font-bold mb-6">Educational Videos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map((i) => (
                <div key={i} className="relative aspect-video bg-[#660000] rounded-xl flex items-center justify-center">
                  <PlayCircle className="w-14 h-14 text-white/70" />
                </div>
              ))}
            </div>
          </section> */}

          {/* Checklist Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-emerald-50 border-emerald-100">
              <CardHeader>
                <CardTitle className="text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> Pre-Procedure Checklist
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p>Initial medical assessment</p>
                <p>Dietary consultation</p>
                <p>Lifestyle guidance</p>
              </CardContent>
            </Card>

            <Card className="bg-orange-50 border-orange-100">
              <CardHeader>
                <CardTitle className="text-orange-800 flex items-center gap-2">
                  <Info className="w-5 h-5" /> Expert Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                Dedicated monitoring and guidance throughout the procedure to ensure safe and effective results.
              </CardContent>
            </Card>
          </div>

        </main>
      </div>
    </div>
  );
};

export default GastricBalloon;